import React, { useMemo, useCallback, useState } from 'react';
import { Form, Button } from 'semantic-ui-react';
import DatePicker from '../../../../DatePicker';
import moment from 'moment';
import styles from './DriverForm.module.scss';
import driverFields from './DriverFields';
import { useMapToOptions } from '../utilities';
import { Field, useErrors, useForm, useIsValid } from '../PolicyForm';
import HelpModal from '../HelpModal';

const DriverForm = ({ data, onChange, isAdd, onSaveAndExit, onContinue, onCancel, onBack, agency, onResetStudentStatus }) => {
	const suffixOptions = useMapToOptions(driverFields.suffix.options, driverFields.suffix.useItem, ' ');
	const maritalStatusOptions = useMapToOptions(driverFields.maritalStatus.options, false, null, driverFields.maritalStatus.base);

	const stateOptions = useMapToOptions(driverFields.stateLicensed.options(isAdd), false, null, driverFields.stateLicensed.base(isAdd));

	const numberOfYearsOptions = useMapToOptions(driverFields.licensedForThreeOrMoreYears.options);
	// const licenseStatusOptions = useMapToOptions(driverFields.licenseStatus.options, false, null, driverFields.licenseStatus.base);

	const relationshipOptions = useMapToOptions(driverFields.relationship.options, false, ' ');
	const studentStatusOptions = useMapToOptions(driverFields.studentStatus.options, false, ' ');

	const driverAge = useMemo(() => moment().diff(moment(data.dateOfBirth), 'years'), [data.dateOfBirth]);

	const isStudentAge = useMemo(() => driverAge >= 16 && driverAge <= 25, [driverAge]);

	const [showErrors, setShowErrors] = useState(false);
	const errors = useErrors(data, driverFields);
	const isValid = useIsValid(errors);
	const form = useForm(driverFields, data, errors, showErrors, onChange);

	const _onContinue = useCallback(() => {
		if (isValid) {
			onContinue();
		} else {
			setShowErrors(true);
		}
	}, [isValid, onContinue]);

	const maxDate = useMemo(() => Date.parse(moment().subtract(16, 'years').format()), []);
	const minDate = useMemo(() => Date.parse(moment().subtract(80, 'years').add(1, 'days').format()), []);

	const [showStudentFields, setShowStudentFields] = useState(false);

	const onNotFullTimeStudent = useCallback(() => {
		setShowStudentFields(false);
		onResetStudentStatus();
	}, [onResetStudentStatus]);

	const onDateOfBirthChange = useCallback((e, data) => {
		const { name, value } = data;
		if (name === 'dateOfBirth') {
			var _driverAge = moment().diff(moment(value), 'years');

			if (_driverAge < 16 || _driverAge > 25) {
				setShowStudentFields(false);
			}
		}

		onChange(e, data);
	}, [onChange]);

	return (
		<Form className={styles.form}>
			<Form.Group inline>
				<label className={styles.required}>First name and middle initial</label>
				<Field name='firstName' {...form} label={null} />
				<Field name='middleInitial' {...form} label={null} />
			</Form.Group>
			<Form.Group inline>
				<label className={styles.required}>Last name and suffix</label>
				<Field name='lastName' {...form} label={null} />
				<Field as={Form.Dropdown} name='suffix' options={suffixOptions} {...form} label={null} selection />
			</Form.Group>

			<Form.Field inline error={showErrors ? errors['dateOfBirth'] : null}>
				<label className={styles.required}>Date of Birth</label>
				<DatePicker id="dateOfBirth" name='dateOfBirth' selected={data.dateOfBirth} dateFormat='MM/dd/yyyy' showMonthDropdown showYearDropdown maxDate={maxDate} minDate={minDate} dropdownMode='select' onChange={onDateOfBirthChange} />
			</Form.Field>
			<Form.Group inline>
				<label className={styles.required}>Gender</label>
				<Form.Radio id="genderMale" name='gender' label='Male' value={1} checked={data.gender === 1} onChange={onChange} />
				<Form.Radio id="genderFemale" name='gender' label='Female' value={2} checked={data.gender === 2} onChange={onChange} />
			</Form.Group>
			<Field as={Form.Dropdown} inline name='maritalStatus' options={maritalStatusOptions} {...form} selection />
			<Field inline name='driversLicenseNumber' {...form} />
			<Field as={Form.Dropdown} inline name='stateLicensed' options={stateOptions} selection {...form} />
			{/* <Field as={Form.Dropdown} inline name='licenseStatus' options={licenseStatusOptions} selection {...form} /> */}
			<Form.Group inline>
				<label className={styles.required}>Is Driver's License Valid?</label>
				<Form.Radio id="isDriversLicenseValidYes" error={showErrors && !data.isDriversLicenseValid} name='isDriversLicenseValid' label='Yes' checked={data.isDriversLicenseValid === true} onClick={(_) => onChange(_, { name: 'isDriversLicenseValid', value: true })} />
				<HelpModal title="Invalid Driver's License" trigger={<Form.Radio id="isDriversLicenseValidNo" name='isDriversLicenseValid' label='No' checked={data.isDriversLicenseValid === false} onClick={(_) => onChange(_, { name: 'isDriversLicenseValid', value: null })} />} agency={agency}>
					Please contact your agent to proceed. The status of the driver's license is prohibiting the completion of this transaction.
				</HelpModal>
			</Form.Group>

			<Field as={Form.Dropdown} inline name='relationship' options={relationshipOptions} selection {...form} />
			<Field as={Form.Dropdown} inline name='licensedForThreeOrMoreYears' options={numberOfYearsOptions} selection {...form} onChange={(_, d) => onChange(_, { ...d, value: !!d.value })} value={data.licensedForThreeOrMoreYears ? 1 : 0} />

			{isStudentAge && (
				<Form.Group inline>
					<label className={styles.required}>Is this driver a Full-Time Student?</label>
					<Form.Radio id="isFullTimeStudentYes" name='isFullTimeStudent' label='Yes' checked={showStudentFields} onChange={() => setShowStudentFields(true)} />
					<Form.Radio id="isFullTimeStudentNo" name='isFullTimeStudent' label='No' checked={!showStudentFields} onChange={onNotFullTimeStudent} />
				</Form.Group>
			)}

			{showStudentFields && (
				<React.Fragment>
					<Field as={Form.Dropdown} inline name='studentStatus' options={studentStatusOptions} selection {...form} />
					<Form.Group inline>
						<label className={styles.required}>Is this driver a full-time student with at least a B average grade level?</label>
						<Form.Radio id="isBLevelStudentYes" name='isBLevelStudent' label='Yes' checked={data.isBLevelStudent === true} onChange={(_) => onChange(_, { name: 'isBLevelStudent', value: true })} />
						<Form.Radio id="isBLevelStudentNo" name='isBLevelStudent' label='No' checked={data.isBLevelStudent === false} onChange={(_) => onChange(_, { name: 'isBLevelStudent', value: false })} />
					</Form.Group>
					{data.isBLevelStudent && <div>If your student has a B average or better, they are eligible for this credit. You may be asked by your agent to provide a copy of the student's grades.</div>}
				</React.Fragment>
			)}

			<div className={styles.buttons}>
				<Button id="continueBtn" primary onClick={_onContinue}>
					Continue
				</Button>
				<Button id="saveAndExitBtn" primary onClick={onSaveAndExit}>
					Save and Exit
				</Button>
				<Button id="cancelBtn" primary onClick={onCancel}>
					Cancel
				</Button>
				<Button id="backBtn" primary onClick={onBack}>
					Back
				</Button>
			</div>
		</Form>
	);
};

DriverForm.propTypes = {};

export default DriverForm;
